/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import type { CSSProperties, ReactNode } from "react";
import {
  CalendarDays,
  ExternalLink,
  Lock,
  Mail,
  MapPin,
  Stethoscope,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { FamilyDetails, FamilyMemberDetail, FamilyPatient } from "../api/accounts";
import type { CareTeamMember } from "../api/careTeam";
import { Badge } from "./ui/Badge";
import { CopyableId } from "./ui/CopyableId";
import { Link } from "./ui/Link";
import { HealthieLink } from "./HealthieLink";
import { formatWhen, fullName, neverRegistered, roleLabel, statusLabel, statusTone } from "../lib/accounts";
import { cn } from "../lib/utils";

const HEADING = "text-sm font-medium uppercase tracking-wide text-text-tertiary";

/**
 * The household as it stands, read-only: who is in it and their account state, the care each
 * child is in, patients with no account yet, and invites still out. The family page and the
 * referral's family peek both draw this, with their own care-team section in the slot. It lays
 * out by its own width, so it reads the same in a sheet as on a page.
 */
export function FamilyGlance({
  details,
  careTeam,
  onCard = false,
}: {
  details: FamilyDetails;
  careTeam: ReactNode;
  /** True on a raised surface, where the chips sit lighter than on the page itself. */
  onCard?: boolean;
}) {
  const patientsByUser = new Map(details.patients.map((patient) => [patient.userId, patient]));
  const orphans = details.patients.filter((patient) => patient.userId === null);
  return (
    <div className="@container space-y-8">
      <div className="enter flex flex-wrap items-center gap-2">
        <StatChip icon={Users} label={memberSummary(details)} quiet={onCard} />
        {details.state !== null && <StatChip icon={MapPin} label={details.state} quiet={onCard} />}
        <StatChip icon={CalendarDays} label={`Since ${formatWhen(details.createdAt)}`} quiet={onCard} />
        {inCareCount(details) > 0 && (
          <StatChip
            icon={Stethoscope}
            label={inCareCount(details) === 1 ? "1 in care" : `${inCareCount(details)} in care`}
            tone="accent"
            quiet={onCard}
          />
        )}
      </div>

      <section className="enter space-y-3" style={{ "--enter-delay": "80ms" } as CSSProperties}>
        <h2 className={HEADING}>Members</h2>
        <ol className="grid gap-4 @3xl:grid-cols-2">
          {details.members.map((member, index) => (
            <MemberCard
              key={member.id}
              member={member}
              patient={patientsByUser.get(member.id) ?? null}
              index={index}
            />
          ))}
        </ol>
      </section>

      {careTeam}

      {orphans.length > 0 && (
        <section className="enter space-y-3" style={{ "--enter-delay": "140ms" } as CSSProperties}>
          <h2 className={HEADING}>Patients without an account</h2>
          <ol className="grid gap-4 @xl:grid-cols-2 @5xl:grid-cols-3">
            {orphans.map((patient) => (
              <li key={patient.id} className="border border-border bg-bg-elevated shadow-card rounded-3xl p-5">
                <div className="flex items-start justify-between gap-3">
                  <span className="inline-flex items-center gap-2 text-sm text-text-secondary">
                    <Stethoscope size={14} />
                    Healthie {patient.externalId ?? "—"}
                  </span>
                  <Badge tone={patient.status === "ACTIVE" ? "success" : "neutral"}>
                    {statusLabel(patient.status)}
                  </Badge>
                </div>
                <CareLine insurance={patient.primaryInsurance} memberId={patient.memberId} />
              </li>
            ))}
          </ol>
        </section>
      )}

      {details.invites.length > 0 && (
        <section className="enter space-y-3" style={{ "--enter-delay": "200ms" } as CSSProperties}>
          <h2 className={HEADING}>Pending invites</h2>
          <ol className="space-y-2">
            {details.invites.map((invite) => (
              <li
                key={invite.id}
                className="border border-border bg-bg-elevated shadow-card flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm"
              >
                <span className="inline-flex min-w-0 items-center gap-2 text-text-secondary">
                  <Mail size={14} className="shrink-0 text-text-tertiary" />
                  <span className="truncate">{invite.email}</span>
                </span>
                <span className="shrink-0 text-xs text-text-tertiary">Sent {formatWhen(invite.createdAt)}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

/** Each child's care team as the family record holds it, with nothing to change here. */
export function CareTeamGlance({ details }: { details: FamilyDetails }) {
  const membersById = new Map(details.members.map((member) => [member.id, member]));
  if (details.careTeam.length === 0) {
    return null;
  }
  return (
    <section className="enter space-y-3" style={{ "--enter-delay": "110ms" } as CSSProperties}>
      <h2 className={HEADING}>Care team</h2>
      <ol className="space-y-3">
        {details.careTeam.map((child) => (
          <li key={child.childUserId}>
            <h3 className="text-sm font-medium text-text-primary">{childName(child.childUserId, membersById)}</h3>
            {child.members.length === 0 ? (
              <p className="mt-1 text-sm text-text-tertiary">No care team yet.</p>
            ) : (
              <ul className="mt-1 divide-y divide-border/60">
                {child.members.map((member) => (
                  <li key={member.staffUserId} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-text-primary">{memberName(member)}</p>
                      <p className="text-xs text-text-tertiary">{statusLabel(member.kind)}</p>
                    </div>
                    {member.assignments.some((assignment) => assignment.primary) && (
                      <Badge tone="info">Primary provider</Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

export function householdName(details: FamilyDetails): string {
  const surname = householdSurname(details);
  return surname === null ? `Family ${details.familyId}` : `The ${surname} family`;
}

/** The first member's surname, the name the household goes by. */
export function householdSurname(details: FamilyDetails): string | null {
  return details.members.map((member) => member.lastName).find((name) => name !== null && name !== "") ?? null;
}

function memberSummary(details: FamilyDetails): string {
  const parents = details.members.filter((member) => member.roles.includes("Parent")).length;
  const kids = details.members.filter((member) => member.roles.includes("Child")).length;
  const parts: string[] = [];
  if (parents > 0) parts.push(parents === 1 ? "1 parent" : `${parents} parents`);
  if (kids > 0) parts.push(kids === 1 ? "1 kid" : `${kids} kids`);
  if (parts.length === 0) {
    return details.members.length === 1 ? "1 member" : `${details.members.length} members`;
  }
  return parts.join(", ");
}

export function memberName(member: CareTeamMember): string {
  return fullName(member.firstName, member.lastName, `Staff ${member.staffUserId}`);
}

export function childName(childUserId: number, membersById: Map<number, FamilyMemberDetail>): string {
  return membersById.get(childUserId)?.firstName ?? `Child ${childUserId}`;
}

/** Adults get Healthie charts too (co-caregiver records); "in care" means the kids. */
function inCareCount(details: FamilyDetails): number {
  const adultIds = new Set(
    details.members.filter((m) => m.roles.includes("Parent")).map((m) => m.id),
  );
  return details.patients.filter(
    (patient) =>
      patient.status !== "DISCHARGED" &&
      (patient.userId === null || !adultIds.has(patient.userId)),
  ).length;
}

function StatChip({
  icon: Icon,
  label,
  tone,
  quiet,
}: {
  icon: LucideIcon;
  label: string;
  tone?: "accent";
  quiet: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs",
        tone === "accent"
          ? cn("font-medium text-accent", quiet ? "bg-accent/6" : "bg-accent/10")
          : cn("text-text-secondary", quiet ? "bg-bg-hover/45" : "bg-bg-hover"),
      )}
    >
      <Icon size={12} />
      {label}
    </span>
  );
}

function MemberCard({
  member,
  patient,
  index,
}: {
  member: FamilyMemberDetail;
  patient: FamilyPatient | null;
  index: number;
}) {
  return (
    <li
      className="group border border-border bg-bg-elevated shadow-card lift enter relative flex h-full flex-col gap-3 rounded-3xl p-5"
      style={{ "--enter-delay": `${Math.min(index, 6) * 55 + 100}ms` } as CSSProperties}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-lg leading-tight text-text-primary">
            <Link
              to={`/accounts/${member.id}`}
              className="after:absolute after:inset-0 after:rounded-3xl"
            >
              {neverRegistered(member.status, member.username)
                ? `Healthie patient ${patient?.externalId ?? member.id}`
                : fullName(member.firstName, member.lastName)}
              {member.nickname !== null && (
                <span className="text-text-tertiary"> “{member.nickname}”</span>
              )}
            </Link>
          </h3>
          <p className="mt-0.5 truncate text-xs text-text-tertiary">
            {member.roles.map(roleLabel).join(" · ")}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {neverRegistered(member.status, member.username) ? (
            <Badge tone="warning">Never registered</Badge>
          ) : (
            <Badge tone={statusTone(member.status)}>{statusLabel(member.status)}</Badge>
          )}
          {member.lockedOut && (
            <Badge tone="error">
              <Lock size={10} className="mr-1 inline-block" />
              Locked out
            </Badge>
          )}
        </div>
      </div>

      <dl className="grid gap-x-6 gap-y-1.5 text-sm @sm:grid-cols-2">
        <ContactFact label={member.email !== null ? "Email" : "Username"}>
          {member.email ?? member.username ?? "—"}
        </ContactFact>
        <ContactFact label="Phone">{member.phone ?? "—"}</ContactFact>
      </dl>

      <div className="relative z-10 flex flex-wrap items-center gap-1.5">
        <CopyableId label="Account" value={String(member.id)} />
        {patient?.externalId != null && (
          <>
            <CopyableId label="Healthie" value={patient.externalId} />
            <HealthieLink
              healthieId={patient.externalId}
              className="inline-flex items-center gap-1 rounded-full bg-bg-hover px-2 py-0.5 text-[11px] text-accent transition-colors hover:bg-accent/10"
            >
              <ExternalLink size={10} />
              Open in Healthie
            </HealthieLink>
          </>
        )}
      </div>

      {patient !== null && (
        <div className="mt-auto space-y-2 border-t border-border/60 pt-3 text-xs text-text-tertiary">
          <span className="inline-flex items-center gap-1.5">
            <Stethoscope size={12} />
            {patient.status === "DISCHARGED" ? "Discharged" : "In care"}
            {(patient.primaryInsuranceName ?? patient.primaryInsurance) !== null &&
              ` · ${patient.primaryInsuranceName ?? patient.primaryInsurance}`}
            {patient.memberId !== null && ` · Member ${patient.memberId}`}
          </span>
          {patient.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {patient.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-bg-hover px-2 py-0.5 text-[10px] text-text-secondary"
                >
                  {tag.trim()}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function ContactFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-text-tertiary">{label}</dt>
      <dd className="truncate text-text-secondary">{children}</dd>
    </div>
  );
}

function CareLine({ insurance, memberId }: { insurance: string | null; memberId: string | null }) {
  if (insurance === null && memberId === null) {
    return null;
  }
  return (
    <p className="mt-3 border-t border-border/60 pt-3 text-xs text-text-tertiary">
      {insurance ?? "No insurance on file"}
      {memberId !== null && ` · ${memberId}`}
    </p>
  );
}
